<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <div class="form-data-box" v-if="groupedLayout" ref="groupedForm">
    <BaseAccordion v-model:open="accordionOpen.personal">
      <template v-slot:header>
        <IconPatientsSimple/>
        <h4>{{ $t('patient.personalInfo') }}</h4>
      </template>

      <template v-slot:content>
        <div class="form-floating">
          <vee-field name="birthday" v-model="patient.birthdayAux" :rules="`date:${$t('dateFormat')}`" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control"  v-maska :data-maska="patient.birthdayAux ? $t('inputMask.date') : ''" :placeholder="$t('patient.birthday')" data-test="inputPatientBirthday"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="birthday">{{ $t('patient.birthday') }}</label>
        </div>
        <div class="form-floating v-select-floating">
          <v-select
            id="gender"
            class="v-select form"
            v-model="patient.gender"
            :reduce="x => x.value"
            :options="genderOptions"
            :placeholder="$t('patient.gender')"
            data-test="vSelectPatientGender"/>
          <label for="gender">{{ $t('patient.gender') }}</label>
        </div>
        <div class="form-floating">
          <vee-field name="firstName" v-model="patient.firstName" rules="required" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="firstName" :placeholder="$t('patient.firstName')" data-test="inputPatientFirstName"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="firstName">{{ $t('patient.firstName') }}*</label>
        </div>
        <div class="form-floating">
          <vee-field name="lastName" v-model="patient.lastName" rules="required" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="lastName" :placeholder="$t('patient.lastName')" data-test="inputPatientLastName"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="lastName">{{ $t('patient.lastName') }}*</label>
        </div>
      </template>
    </BaseAccordion>

    <BaseAccordion v-model:open="accordionOpen.contact">
      <template v-slot:header>
        <IconPatientsSimple/>
        <h4>{{ $t('patient.contact') }}</h4>
      </template>

      <template v-slot:content>
        <div class="form-floating">
          <input type="text" class="form-control" id="telephone1" v-model="patient.telephone2" :placeholder="$t('patient.telephone1')" data-test="inputPatientPhone1"/>
          <label for="telephone1">{{ $t('patient.telephone1') }}</label>
        </div>
        <div class="form-floating">
          <vee-field name="email" v-model="patient.email" rules="email" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" ref="email" id="email" :placeholder="$t('createClinic.email')" data-test="inputPatientEmail"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="email">{{ $t('createClinic.email') }}</label>
        </div>
      </template>
    </BaseAccordion>

    <BaseAccordion v-model:open="accordionOpen.documents">
      <template v-slot:header>
        <IconDocuments/>
        <h4>{{ $t('patient.documents') }}</h4>
      </template>

      <template v-slot:content>
        <div class="form-floating">
          <vee-field name="mrn" v-model="patient.mrn" :rules="`${mandatoryDocument === 'mrn' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="mrn" :placeholder="$t('mrn')" data-test="inputPatientMrn"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="mrn">
            {{ $t('mrn') }}
            <span v-if="mandatoryDocument === 'mrn'">*</span>
          </label>
        </div>
        <div class="form-floating">
          <vee-field name="document0" v-model="patient.document0" :rules="`${mandatoryDocument === 'document0' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="document0" :placeholder="$t('document0')" data-test="inputPatientDocument0"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="document0">
            {{ $t('document0') }}
            <span v-if="mandatoryDocument === 'document0'">*</span>
          </label>
        </div>
        <div class="form-floating">
          <vee-field name="document1" v-model="patient.document1" :rules="`${mandatoryDocument === 'document1' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="document1" :placeholder="$t('document1')" data-test="inputPatientDocument1"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="document1">
            {{ $t('document1') }}
            <span v-if="mandatoryDocument === 'document1'">*</span>
          </label>
        </div>
        <div class="form-floating">
          <vee-field name="document2" v-model="patient.document2" :rules="`${mandatoryDocument === 'document2' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="document2" v-maska :data-maska="$t('inputMask.document2')" :placeholder="$t('document2')" data-test="inputPatientDocument2"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="document2">
            {{ $t('document2') }}
            <span v-if="mandatoryDocument === 'document2'">*</span>
          </label>
        </div>
        <div class="form-floating">
          <vee-field name="document3" v-model="patient.document3" :rules="`${mandatoryDocument === 'document3' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="document3" :placeholder="$t('document3')" data-test="inputPatientDocument3"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="document3">
            {{ $t('document3') }}
            <span v-if="mandatoryDocument === 'document3'">*</span>
          </label>
        </div>
        <div class="form-floating">
          <vee-field name="document4" v-model="patient.document4" :rules="`${mandatoryDocument === 'document4' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
            <input v-bind="field" type="text" class="form-control" id="document4" :placeholder="$t('document4')" data-test="inputPatientDocument4"/>
            <span class="vee-error">{{ errorMessage }}</span>
          </vee-field>
          <label for="document4">
            {{ $t('document4') }}
            <span v-if="mandatoryDocument === 'document4'">*</span>
          </label>
        </div>
      </template>
    </BaseAccordion>

    <BaseAccordion v-if="underlyingChange || ophthalmicChange" v-model:open="accordionOpen.clinical">
      <template v-slot:header>
        <IconClinicInfo/>
        <h4>{{ $t('patient.clinicalInfo') }}</h4>
      </template>

      <template v-slot:content>
        <PatientCreateUnderlyingDiseases v-if="underlyingChange" :underlying="underlying"/>
        <PatientCreateOphthalmicDiseases v-if="ophthalmicChange" :ophthalmic="ophthalmic"/>
      </template>
    </BaseAccordion>

    <BaseAccordion v-model:open="accordionOpen.diopter">
      <template v-slot:header>
        <IconDiopter/>
        <h4>{{ $t('patient.diopter') }}</h4>
      </template>

      <template v-slot:content>
        <div class="row">
          <div class="col-lg-6">
            <h3 class="sub-title">{{ $t('patient.rightEyeDiopter') }}</h3>
            <div class="form-floating">
              <input type="text" class="form-control" id="right_spherical" v-maska maska-diopter v-model="rightDiopter.spherical" :placeholder="$t('spherical')" data-test="inputPatientRightDiopterSpherical"/>
              <label for="right_spherical">{{ $t('spherical') }}</label>
            </div>
            <div class="form-floating">
              <input type="text" class="form-control" id="right_cylindrical" v-maska maska-diopter v-model="rightDiopter.cylindrical" :placeholder="$t('cylindrical')" data-test="inputPatientRightDiopterCylindrical"/>
              <label for="right_cylindrical">{{ $t('cylindrical') }}</label>
            </div>
            <div class="form-floating">
              <input type="text" class="form-control" id="right_axis" v-maska :data-maska="$t('inputMask.axis')" v-model="rightDiopter.axis" :placeholder="$t('axis')" data-test="inputPatientRightDiopterAxis"/>
              <label for="right_axis">{{ $t('axis') }}</label>
            </div>
          </div>

          <div class="col-lg-6">
            <h3 class="sub-title">{{ $t('patient.leftEyeDiopter') }}</h3>
            <div class="form-floating">
              <input type="text" class="form-control" id="left_spherical" v-maska maska-diopter v-model="leftDiopter.spherical" :placeholder="$t('spherical')" data-test="inputPatientLeftDiopterSpherical"/>
              <label for="left_spherical">{{ $t('spherical') }}</label>
            </div>
            <div class="form-floating">
              <input type="text" class="form-control" id="left_cylindrical" v-maska maska-diopter v-model="leftDiopter.cylindrical" :placeholder="$t('cylindrical')" data-test="inputPatientLeftDiopterCylindrical"/>
              <label for="left_cylindrical">{{ $t('cylindrical') }}</label>
            </div>
            <div class="form-floating">
              <input type="text" class="form-control" id="left_axis" v-maska :data-maska="$t('inputMask.axis')" v-model="leftDiopter.axis" :placeholder="$t('axis')" data-test="inputPatientLeftDiopterAxis"/>
              <label for="left_axis">{{ $t('axis') }}</label>
            </div>
          </div>
        </div>
      </template>
    </BaseAccordion>

    <BaseAccordion v-if="patient.anamnesis" v-model:open="accordionOpen.observations">
      <template v-slot:header>
        <IconObservation/>
        <h4>{{ $t('patient.observations') }}</h4>
      </template>

      <template v-slot:content>
        <div class="form-floating">
          <textarea name="name" class="form-control" id="otherObservation" v-model="patient.anamnesis.otherDisease" :placeholder="$t('patient.other')" data-test="textareaPatientAnamnesisOtherDisease"></textarea>
          <label for="otherObservation">{{ $t('patient.other') }}</label>
        </div>
      </template>
    </BaseAccordion>

    <BaseAccordion v-model:open="accordionOpen.location">
      <template v-slot:header>
        <IconLocale/>
        <h4>{{ $t('patient.location') }}</h4>
      </template>

      <template v-slot:content>
        <div class="form-floating">
          <input type="text" class="form-control" id="address" v-model="patient.address" :placeholder="$t('patient.address')" data-test="inputPatientAddress"/>
          <label for="address">{{ $t('patient.address') }}</label>
        </div>
        <div class="form-floating">
          <input type="text" class="form-control" id="addressNumber" v-model="patient.addressNumber" :placeholder="$t('patient.addressNumber')" data-test="inputPatientAddressNumber"/>
          <label for="addressNumber">{{ $t('patient.addressNumber') }}</label>
        </div>
        <div class="form-floating">
          <input type="text" class="form-control" id="district" v-model="patient.district" :placeholder="$t('patient.district')" data-test="inputPatientDistrict"/>
          <label for="district">{{ $t('patient.district') }}</label>
        </div>
        <div class="form-floating">
          <input type="text" class="form-control" id="postalCode" v-model="patient.postalCode" :placeholder="$t('patient.postalCode')" data-test="inputPatientPostalCode"/>
          <label for="postalCode">{{ $t('patient.postalCode') }}</label>
        </div>
        <div class="form-floating">
          <input type="text" class="form-control" id="city" v-model="patient.city" :placeholder="$t('patient.city')" data-test="inputPatientCity"/>
          <label for="city">{{ $t('patient.city') }}</label>
        </div>
        <div class="form-floating">
          <input type="text" class="form-control" id="state" v-model="patient.state" :placeholder="$t('patient.state')" data-test="inputPatientState"/>
          <label for="state">{{ $t('patient.state') }}</label>
        </div>
        <div class="form-floating">
          <input type="text" class="form-control" id="country" v-model="patient.country" :placeholder="$t('patient.country')" data-test="inputPatientCountry"/>
          <label for="country">{{ $t('patient.country') }}</label>
        </div>
      </template>
    </BaseAccordion>
  </div>

  <div class="form-data-box" v-else>
    <section id="general">
      <!-- <h3 class="sub-title">{{ $t('patient.patientInfo') }}</h3> -->
      <div class="form-floating">
        <vee-field name="firstName" v-model="patient.firstName" rules="required" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="firstName" :placeholder="$t('patient.firstName')" data-test="inputPatientFirstName"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="firstName">{{ $t('patient.firstName') }}*</label>
      </div>
      <div class="form-floating">
        <vee-field name="lastName" v-model="patient.lastName" rules="required" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="lastName" :placeholder="$t('patient.lastName')" data-test="inputPatientLastName"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="lastName">{{ $t('patient.lastName') }}*</label>
      </div>
      <div class="form-floating">
        <vee-field name="birthday" v-model="patient.birthdayAux" :rules="`date:${$t('dateFormat')}`" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control"  v-maska :data-maska="patient.birthdayAux ? $t('inputMask.date') : ''" :placeholder="$t('patient.birthday')" data-test="inputPatientBirthday"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="birthday">{{ $t('patient.birthday') }}</label>
      </div>
      <div class="form-floating">
        <input type="text" class="form-control" id="telephone1" v-model="patient.telephone2" :placeholder="$t('patient.telephone1')" data-test="inputPatientPhone1"/>
        <label for="telephone1">{{ $t('patient.telephone1') }}</label>
      </div>
      <div class="form-floating">
        <vee-field name="email" v-model="patient.email" rules="email" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" ref="email" id="email" :placeholder="$t('createClinic.email')" data-test="inputPatientEmail"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="email">{{ $t('createClinic.email') }}</label>
      </div>
      <div class="form-floating">
        <vee-field name="mrn" v-model="patient.mrn" :rules="`${mandatoryDocument === 'mrn' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="mrn" :placeholder="$t('mrn')" data-test="inputPatientMrn"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="mrn">
          {{ $t('mrn') }}
          <span v-if="mandatoryDocument === 'mrn'">*</span>
        </label>
      </div>
      <div class="form-floating">
        <vee-field name="document0" v-model="patient.document0" :rules="`${mandatoryDocument === 'document0' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="document0" :placeholder="$t('document0')" data-test="inputPatientDocument0"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="document0">
          {{ $t('document0') }}
          <span v-if="mandatoryDocument === 'document0'">*</span>
        </label>
      </div>
      <div class="form-floating">
        <vee-field name="document1" v-model="patient.document1" :rules="`${mandatoryDocument === 'document1' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="document1" :placeholder="$t('document1')" data-test="inputPatientDocument1"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="document1">
          {{ $t('document1') }}
          <span v-if="mandatoryDocument === 'document1'">*</span>
        </label>
      </div>
      <div class="form-floating">
        <vee-field name="document2" v-model="patient.document2" :rules="`${mandatoryDocument === 'document2' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="document2" v-maska :data-maska="$t('inputMask.document2')" :placeholder="$t('document2')" data-test="inputPatientDocument2"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="document2">
          {{ $t('document2') }}
          <span v-if="mandatoryDocument === 'document2'">*</span>
        </label>
      </div>
      <div class="form-floating">
        <vee-field name="document3" v-model="patient.document3" :rules="`${mandatoryDocument === 'document3' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="document3" :placeholder="$t('document3')" data-test="inputPatientDocument3"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="document3">
          {{ $t('document3') }}
          <span v-if="mandatoryDocument === 'document3'">*</span>
        </label>
      </div>
      <div class="form-floating">
        <vee-field name="document4" v-model="patient.document4" :rules="`${mandatoryDocument === 'document4' ? 'required|min:1' : ''}`" v-slot="{ field, errorMessage }">
          <input v-bind="field" type="text" class="form-control" id="document4" :placeholder="$t('document4')" data-test="inputPatientDocument4"/>
          <span class="vee-error">{{ errorMessage }}</span>
        </vee-field>
        <label for="document4">
          {{ $t('document4') }}
          <span v-if="mandatoryDocument === 'document4'">*</span>
        </label>
      </div>
      <div class="form-floating v-select-floating">
        <v-select
          id="gender"
          class="v-select form"
          v-model="patient.gender"
          :reduce="x => x.value"
          :options="genderOptions"
          :placeholder="$t('patient.gender')"
          data-test="vSelectPatientGender"/>
        <label for="gender">{{ $t('patient.gender') }}</label>
      </div>
    </section>

    <section id="underlying" v-if="underlyingChange">
      <PatientCreateUnderlyingDiseases :underlying="underlying"/>
    </section>

    <section id="ophthalmic" v-if="ophthalmicChange">
      <PatientCreateOphthalmicDiseases :ophthalmic="ophthalmic"/>
    </section>

    <section id="other" class="localization-pb-33" v-if="patient.anamnesis">
      <h3 class="sub-title">{{ $t('patient.other') }}</h3>
      <div class="form-floating">
        <textarea name="name" class="form-control" id="otherObservation" v-model="patient.anamnesis.otherDisease" :placeholder="$t('patient.other')" data-test="textareaPatientAnamnesisOtherDisease"></textarea>
        <label for="otherObservation">{{ $t('patient.other') }}</label>
      </div>
    </section>

    <section id="diopter">
      <div class="row">
        <!-- TODO componente -> dioptria: map{foo: bar, foo1: bar1} -->
        <div class="col-lg-6">
          <h3 class="sub-title">{{ $t('patient.rightEyeDiopter') }}</h3>
          <div class="form-floating">
            <input type="text" class="form-control" id="right_spherical" v-maska maska-diopter v-model="rightDiopter.spherical" :placeholder="$t('spherical')" data-test="inputPatientRightDiopterSpherical"/>
            <label for="right_spherical">{{ $t('spherical') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="right_cylindrical" v-maska maska-diopter v-model="rightDiopter.cylindrical" :placeholder="$t('cylindrical')" data-test="inputPatientRightDiopterCylindrical"/>
            <label for="right_cylindrical">{{ $t('cylindrical') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="right_axis" v-maska :data-maska="$t('inputMask.axis')" v-model="rightDiopter.axis" :placeholder="$t('axis')" data-test="inputPatientRightDiopterAxis"/>
            <label for="right_axis">{{ $t('axis') }}</label>
          </div>
        </div>

        <div class="col-lg-6">
          <h3 class="sub-title">{{ $t('patient.leftEyeDiopter') }}</h3>
          <div class="form-floating">
            <input type="text" class="form-control" id="left_spherical" v-maska maska-diopter v-model="leftDiopter.spherical" :placeholder="$t('spherical')" data-test="inputPatientLeftDiopterSpherical"/>
            <label for="left_spherical">{{ $t('spherical') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="left_cylindrical" v-maska maska-diopter v-model="leftDiopter.cylindrical" :placeholder="$t('cylindrical')" data-test="inputPatientLeftDiopterCylindrical"/>
            <label for="left_cylindrical">{{ $t('cylindrical') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="left_axis" v-maska :data-maska="$t('inputMask.axis')" v-model="leftDiopter.axis" :placeholder="$t('axis')" data-test="inputPatientLeftDiopterAxis"/>
            <label for="left_axis">{{ $t('axis') }}</label>
          </div>
        </div>
      </div>
    </section>

    <section id="localization">
      <h3 class="sub-title">{{ $t('patient.location') }}</h3>
      <div class="form-floating">
        <input type="text" class="form-control" id="address" v-model="patient.address" :placeholder="$t('patient.address')" data-test="inputPatientAddress"/>
        <label for="address">{{ $t('patient.address') }}</label>
      </div>
      <div class="form-floating">
        <input type="text" class="form-control" id="addressNumber" v-model="patient.addressNumber" :placeholder="$t('patient.addressNumber')" data-test="inputPatientAddressNumber"/>
        <label for="addressNumber">{{ $t('patient.addressNumber') }}</label>
      </div>
      <div class="form-floating">
        <input type="text" class="form-control" id="district" v-model="patient.district" :placeholder="$t('patient.district')" data-test="inputPatientDistrict"/>
        <label for="district">{{ $t('patient.district') }}</label>
      </div>
      <div class="form-floating">
        <input type="text" class="form-control" id="postalCode" v-model="patient.postalCode" :placeholder="$t('patient.postalCode')" data-test="inputPatientPostalCode"/>
        <label for="postalCode">{{ $t('patient.postalCode') }}</label>
      </div>
      <div class="form-floating">
        <input type="text" class="form-control" id="city" v-model="patient.city" :placeholder="$t('patient.city')" data-test="inputPatientCity"/>
        <label for="city">{{ $t('patient.city') }}</label>
      </div>
      <div class="form-floating">
        <input type="text" class="form-control" id="state" v-model="patient.state" :placeholder="$t('patient.state')" data-test="inputPatientState"/>
        <label for="state">{{ $t('patient.state') }}</label>
      </div>
      <div class="form-floating">
        <input type="text" class="form-control" id="country" v-model="patient.country" :placeholder="$t('patient.country')" data-test="inputPatientCountry"/>
        <label for="country">{{ $t('patient.country') }}</label>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.form-check {
  margin:                      0;

  &:not(:last-child) {
    margin-bottom:             1rem;
  }

  input {
    width:                     24px;
    height:                    24px;
    margin:                    0;
  }
}

section {
  display:                     flex;
  flex-direction:              column;
  gap:                         15px;
}

.form-data-box {
  --vs-selected-color:                    var(--Brand-Primary);
  --vs-search-input-color:                var(--Brand-Primary);
  --vs-search-input-placeholder-color:    var(--Shades-50);
  --vs-dropdown-bg:                       var(--Shades-BG);
  --vs-dropdown-color:                    var(--Brand-Primary);
  --vs-dropdown-option-color:             var(--Brand-Primary);
  --vs-dropdown-option--active-bg:        var(--Shades-10);
  --vs-dropdown-option--active-color:     var(--Brand-Primary);

  :deep(.accordion.current .accordion-content) {
    display:                   flex !important;
    flex-direction:            column;
    gap:                       15px;
  }
}

.action-buttons {
  min-height:                  39px;
}

.vee-error {
  width:                       100% !important;
}

@media (max-width: 991px) {
  .sub-title {
    font-size:                 14px;
  }
}
</style>

<script>
  import { useUserStore } from '@/store/user'
  import { MaskInput } from 'maska'
  import PatientCreateOphthalmicDiseases from './PatientCreateOphthalmicDiseases.vue'
  import PatientCreateUnderlyingDiseases from './PatientCreateUnderlyingDiseases.vue'

  export default {
    name: 'PatientFormData',
    props: {
      patient: { type: Object, default: null },
      rightDiopter: { type: Object, default: null },
      leftDiopter: { type: Object, default: null },
      underlying: { type: Object, default: null },
      ophthalmic: { type: Object, default: null },
      disabled: { type: Boolean, default: false },
      underlyingChange: { type: Boolean, default: false },
      ophthalmicChange: { type: Boolean, default: false },
      groupedLayout: { type: Boolean, default: false },
    },
    components: {
      PatientCreateOphthalmicDiseases,
      PatientCreateUnderlyingDiseases,
    },
    data: () => ({
      mandatoryDocument: null,
      genderOptions: [],
      session: {
        collaborator: {},
        currentClinic: {}
      },
      accordionOpen: {
        personal: true,
        contact: true,
        documents: false,
        clinical: false,
        diopter: false,
        observations: false,
        location: false,
      },
      fieldAccordionMap: {
        birthday: 'personal',
        firstName: 'personal',
        lastName: 'personal',
        email: 'contact',
        mrn: 'documents',
        document0: 'documents',
        document1: 'documents',
        document2: 'documents',
        document3: 'documents',
        document4: 'documents',
      },
    }),
    created() {
      this.genderOptions = [
        {value: 'M', label: this.$i18n.t('patient.male')},
        {value: 'F', label: this.$i18n.t('patient.female')},
        {value: 'NA', label: this.$i18n.t('patient.notInform')},
      ]
    },
    mounted() {
      const userStore = useUserStore()
      this.session = userStore.sessionInfo

      this.mandatoryDocument = this.session.currentClinic.mandatoryDocument

      this.initializeMaskInput()
    },
    methods: {
      initializeMaskInput() {
        new MaskInput("[maska-diopter]", {
          mask: (val) => {
            const isNegative = val.startsWith('-');
            const absVal = isNegative ? val.slice(1) : val;

            const lengthMap = {
              2: "inputMask.minDiopter",
              3: "inputMask.shortDiopter",
              4: "inputMask.shortDiopter"
            };

            return `${isNegative ? "-" : ""}${this.$i18n.t(lengthMap[absVal.length] || "inputMask.diopter")}`;
          }
        });
      },
      revealFirstInvalidField(fieldNames) {
        if (!fieldNames || !fieldNames.length)
          return

        for (let fieldName of fieldNames) {
          const accordionKey = this.fieldAccordionMap[fieldName]

          if (accordionKey)
            this.accordionOpen[accordionKey] = true
        }

        this.$nextTick(() => {
          const selector = fieldNames.map(name => `[name="${CSS.escape(name)}"]`).join(',')
          this.$refs.groupedForm?.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      },
    }
  }
</script>
